<template>
    <div class="autocomplete">
        <fieldset>
            <template>
                <div class="input-group">
                    <div class="input-wrapper-lg street1-wrapper">
                        <label for="street1">*{{ isGift ? 'Their' : 'Shipping'}} Address</label>
                        <input id="street1" type="text" v-model="street1" v-on:keyup="e => {if(e.target.id === 'street1') {queryAutocompleteForSuggestions(e.target.value)}}">
                        <ul  v-if="suggestions && suggestions.length" class="auto-complete-menu" v-click-outside="closeSuggestions">
                            <li v-for="suggestion in suggestions" class="suggestion" @click="useAutoCompleteSuggestion(suggestion)">{{ suggestion.streetLine }} {{ suggestion.secondary }} {{ suggestion.city }}, {{ suggestion.state }}, {{ suggestion.zipcode }}</li>
                        </ul>
                    </div>
                </div>

                <div class="input-group">
                    <div class="input-wrapper-lg">
                        <label for="street2">Apt / Suite</label>
                        <input id="street2" type="text" v-model="street2">
                    </div>
                </div>

                <div class="input-group">
                    <div class="input-wrapper-lg">
                        <label for="city">*City</label>
                        <input id="city" type="text" v-model="city">
                    </div>
                </div>

                <div class="input-group">
                    <div class="input-wrapper-md">
                        <label for="state">*State</label>
                        <select id="state" v-model="state">
                            <option v-for="state in getUsStateList()" :value="state.key">{{ state.value }}</option>
                        </select>
                    </div>

                    <div class="input-wrapper-md">
                        <label for="postal-code">*Zip</label>
                        <input id="postal-code" type="text" v-model="postalCode" @keypress="restrictCharacters" v-on:blur="$emit('postal-code-changed', postalCode)">
                    </div>
                </div>
            </template>
        </fieldset>

        <div v-if="error" class="font-red-light align-center">
            <p class="no-margin">{{ errorMessage }}
                <span v-if="error === 'invalid'" class="address-error-option" @click="emitWithoutValidation()">Proceed anyway?</span>
                <span v-if="error === 'ambiguous' && candidate" class="address-error-option" @click="correctAddress()">Correct</span>
            </p>
        </div>

    </div>
</template>

<script>
    import * as SmartyStreetsSDK from "smartystreets-javascript-sdk";
    import * as sdkUtils from "smartystreets-javascript-sdk-utils";
    import LocationMixin from  '../../mixins/location-mixin';
    import StringSimilarity from 'string-similarity';
    import { ClickOutside }from '../../directives/click-outside';
    import _ from 'lodash'

    // SmartyStreet SDK
    const SmartyStreetsCore = SmartyStreetsSDK.core;
    const Lookup = SmartyStreetsSDK.usAutocompletePro.Lookup;
    const websiteKey = "2854876328098293397";
    const smartyStreetsSharedCredentials = new SmartyStreetsCore.SharedCredentials(websiteKey);
    const autoCompleteClient = SmartyStreetsCore.buildClient.usAutocompletePro(smartyStreetsSharedCredentials);
    const usStreetClientBuilder = new SmartyStreetsCore.ClientBuilder(smartyStreetsSharedCredentials);
    const usStreetClient = usStreetClientBuilder.buildUsStreetApiClient();

    export default {
        props: {
            isGift: Boolean,
            userAddress: Object,
            subscribe: Boolean,
            signUp: Boolean,
            onboarding: Boolean
        },
        mixins: [
            LocationMixin
        ],
        directives: {
            ClickOutside
        },
        data: function() {
            return {
                shouldValidate: true,
                street1: undefined,
                street2: undefined,
                city: undefined,
                state: undefined,
                postalCode: undefined,
                country: "US",
                suggestions: [],
                error: undefined,
                candidate: undefined,
                isValid: false,
                skipValidation: false
            };
        },
        mounted() {
            this.loadFormValues();
        },
        watch: {
            // Watch for changes in form fields and save data to Local Storage
            street1: 'saveFormValues',
            street2: 'saveFormValues',
            city: 'saveFormValues',
            state: 'saveFormValues',
            postalCode: 'saveFormValues'
        },
        methods: {
            saveFormValues() {
              const formData = {
                street1: this.street1,
                street2: this.street2,
                city: this.city,
                state: this.state,
                postalCode: this.postalCode
              };
              localStorage.setItem('formValues', JSON.stringify(formData));
            },
            loadFormValues() {
              const savedFormValues = localStorage.getItem('formValues');
                  if (savedFormValues) {
                    const parsedFormValues = JSON.parse(savedFormValues);
                    Object.assign(this, parsedFormValues);
                  }
            },
            queryAutocompleteForSuggestions: _.debounce( function (query) {
                if (!query || query.length < 3) {
                    return;
                }

                const lookup = new Lookup(query);

                autoCompleteClient.send(lookup)
                    .then(response => {
                        this.suggestions = response.result;
                    })
                    .catch((e) => this.error = e.error);
            }, 300),
            useAutoCompleteSuggestion(suggestion) {
                this.street1 = suggestion.streetLine;
                this.street2 = suggestion.secondary;
                this.city = suggestion.city;
                this.state = suggestion.state;
                this.postalCode = suggestion.zipcode;
                this.suggestions = [];

                this.$emit('postal-code-changed', this.postalCode)
            },
            validateAddress() {
                this.isValid = false;

                if (this.isGift && this.userAddress) {
                    if (this.isFriendAddressSameAsGifterAddress()) {
                        this.error = 'Friend\'s address must be different from yours.';
                        this.$emit('validation-failed');
                        return;
                    }
                }

                let lookup = new SmartyStreetsSDK.usStreet.Lookup();
                lookup.street = this.street1;
                lookup.street2 = this.street2;
                lookup.city = this.city;
                lookup.state = this.state;
                lookup.zipcode = this.postalCode;
                if (!!lookup.street && !!lookup.city && !!lookup.state && !!lookup.zipcode) {
                    usStreetClient.send(lookup)
                        .then(this.updateStateFromValidatedAddress)
                        .catch(e => this.error = e.error);
                } else {
                    // An incomplete address is an error on every flow, including
                    // onboarding sign-up.
                    //
                    // This used to branch on `this.onboarding && this.sign` and
                    // call emptyAddress(), which submitted a single space (" ")
                    // for every address field plus validationSkipped: true. That
                    // was how an account could be created through the real UI
                    // with no address at all -- just leave the fields blank and
                    // press "Create Free Account". The server now rejects a
                    // whitespace-only address as well (User::SHIPPING_FIELDS
                    // presence), so this is the matching client-side message
                    // rather than the only line of defence.
                    this.error = "The address is incomplete.";
                    // The parent resets `processing` (and so un-freezes the
                    // Continue button) only on this event. Without it the
                    // spinner runs forever and the form looks hung rather than
                    // invalid. Pre-existing omission, but this branch now
                    // carries the traffic the emptyAddress() escape hatch used
                    // to swallow, so it is reached far more often.
                    this.$emit('validation-failed');
                }
            },
            updateStateFromValidatedAddress(response) {
                const lookup = response.lookups[0];
                const isValid = sdkUtils.isValid(lookup);
                const isAmbiguous = sdkUtils.isAmbiguous(lookup);
                const isMissingSecondary = sdkUtils.isMissingSecondary(lookup);

                if (!isValid) {
                    this.error = 'invalid';
                    this.$emit('validation-failed');
                } else if (isAmbiguous) {
                    this.candidate = lookup.result[0];
                    this.error = 'ambiguous';
                    this.$emit('validation-failed');
                } else if (isMissingSecondary) {
                    this.error = "The address is missing a secondary number.";
                    this.$emit('validation-failed');
                } else if (isValid) {
                    // const candidate = lookup.result[0];
                    // this.street1 = candidate.deliveryLine1;
                    // this.street2 = candidate.deliveryLine2;
                    // this.city = candidate.components.cityName;
                    // this.state = candidate.components.state;
                    // this.postalCode = candidate.components.zipCode + "-" + candidate.components.plus4Code;

                    this.error = undefined;
                    this.isValid = true;

                    this.$emit('validated', {
                        street1: this.street1,
                        street2: this.street2 != undefined ? this.street2 : " ",
                        city: this.city,
                        state: this.state,
                        postalCode: this.postalCode,
                        country: 'US'
                    });
                }
            },
            correctAddress() {
                this.street1 = this.candidate.deliveryLine1;
                this.street2 = this.candidate.deliveryLine2;
                this.city = this.candidate.components.cityName;
                this.state = this.candidate.components.state;
                this.postalCode = this.candidate.components.zipcode + "-" + this.candidate.components.plus4Code;
                this.error = undefined;
                this.isValid = true;
            },
            restrictCharacters(evt) {
                // Allow numbers and '-' input only
                evt = (evt) ? evt : window.event;
                let charCode = (evt.which) ? evt.which : evt.keyCode;
                if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 45 && charCode !== 46 ) {
                    evt.preventDefault();
                } else {
                    return true;
                }
            },
            assignAddress(address) {
                if(!this.street1) this.street1 = address.street1;
                if(!this.street2) this.street2 = address.street2;
                if(!this.city) this.city = address.city;
                if(!this.state) this.state = address.state;
                if(!this.postalCode) this.postalCode = address.postalCode;
            },
            closeSuggestions() {
                this.suggestions = undefined;
            },
            emitWithoutValidation() {
                this.$emit('validated', {
                    street1: this.street1,
                    street2: this.street2,
                    city: this.city,
                    state: this.state,
                    postalCode: this.postalCode,
                    country: 'US',
                    validationSkipped: true
                });
            },
            isFriendAddressSameAsGifterAddress() {
                if (this.userAddress.state !== this.state) {
                    return false;
                }

                const userAddressString = this.userAddress.street1 + (this.userAddress.street2 || '') + this.city;
                const friendAddressString = this.street1 + (this.street2 || '')  + this.city;
                const similarity = StringSimilarity.compareTwoStrings(userAddressString, friendAddressString);
                return similarity > 0.9
            }
        },
        computed: {
            errorMessage() {
                switch(this.error) {
                    case 'invalid':
                        return 'Hmm, the address is invalid.';
                    case 'ambiguous':
                        return 'The address is ambiguous.';
                    default:
                        return this.error
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../styles/_variables_home.scss";
    @import "../../styles/common_home.scss";
    @import "../../styles/form_home.scss";

    .street1-wrapper {
        position: relative;
    }
    .auto-complete-menu {
        background-color: #fefefe;
        border: 2px solid #eef1f3;
        list-style-type: none;
        width: 100%;
        margin: 0;
        padding: 0;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 4px 6px 0 rgba(50, 50, 93, 0.11);
        color: #0a0a0a;
        font-size: 16px;
        position: absolute;
        top: 45px;
        text-align: left;
        z-index: 10;
    }
    .suggestion {
        cursor: pointer;
        padding: 6px;
        &:hover {
            background-color: #EEE;
        }
    }
    .address-error-option {
        text-decoration: underline;
        cursor: pointer;
        &:hover {
            color: $red;
        }
    }
</style>
